import React, { ReactNode, useCallback, useMemo } from 'react'
import { Tabs } from '@/components/ui/tabs'
import { TabGroup } from '@/components/ui/tab-group'
import { Button } from '@/components/ui/button'
import { Filter, X, LucideIcon } from 'lucide-react'
import { TabConfig } from '@/lib/config/tabs'
import {
  ListPageLayout,
  EmptyState,
  LoadingState,
  PageHeader,
  PageContainer,
} from '@/components/layout'

// Generic interfaces for node configuration
export interface BaseNodeFiltersState {
  [key: string]: string | number | symbol
}

export interface BaseNodeGroupingProps {
  className?: string
  [key: string]: unknown
}

// Configuration interface for node-specific behavior
export interface NodeListConfig<
  TNode,
  TFilters extends BaseNodeFiltersState,
  TGroupBy extends string,
> {
  // Node identification
  nodeName: string
  nodeNamePlural: string

  // Page configuration
  title: string
  description: string
  searchPlaceholder: string
  createAction?: {
    label: string
    icon: LucideIcon
    href: string
  }

  // Tab configuration (optional)
  tabs?: TabConfig[]
  defaultTab?: string

  // Filter configuration
  FilterComponent: React.ComponentType<
    {
      filters: TFilters
      onFiltersChange: (filters: Partial<TFilters>) => void
      className?: string
    } & Record<string, unknown>
  >
  defaultFilters: TFilters

  // Grouping configuration
  GroupingComponent: React.ComponentType<
    {
      nodes?: TNode[]
      tasks?: TNode[]
      projects?: TNode[]
      messages?: TNode[]
      groupBy: TGroupBy
    } & BaseNodeGroupingProps
  >
  defaultGroupBy: TGroupBy
  GroupingSelectorComponent: React.ComponentType<{
    value: TGroupBy
    onChange: (value: TGroupBy) => void
  }>
  // Specify which prop name to use for the data array (e.g., 'tasks', 'projects', 'messages')
  dataPropName: 'nodes' | 'tasks' | 'projects' | 'messages'

  // Data processing
  filterNodes: (
    nodes: TNode[],
    activeTab: string | undefined,
    filters: TFilters,
    searchQuery: string,
    user?: { id: string; role: string }
  ) => TNode[]

  // Optional sorting function
  sortNodes?: (nodes: TNode[]) => TNode[]

  // Optional node-specific props for filter/grouping components
  filterComponentProps?: Record<string, unknown>
  groupingComponentProps?: Record<string, unknown>

  // Empty state configuration
  emptyStateIcon?: LucideIcon
  emptyStateTitle?: string
  emptyStateDescription?: string
}

// Props for the NodeListPage component
export interface NodeListPageProps<
  TNode,
  TFilters extends BaseNodeFiltersState,
  TGroupBy extends string,
> {
  // Data
  nodes: TNode[]
  loading: boolean

  // State
  searchQuery?: string
  onSearchChange?: (query: string) => void
  activeTab?: string
  onTabChange?: (tab: string) => void
  showFilters: boolean
  onFiltersToggle: () => void
  filters: TFilters
  onFiltersChange: (filters: Partial<TFilters>) => void
  groupBy: TGroupBy
  onGroupByChange: (groupBy: TGroupBy) => void

  // Configuration
  config: NodeListConfig<TNode, TFilters, TGroupBy>

  // Optional override for empty state
  emptyStateTitle?: string
  emptyStateDescription?: string

  // Additional actions to show in the actions bar
  additionalActions?: ReactNode

  // Custom className
  className?: string

  // Disable search integration for search results pages
  disableSearchIntegration?: boolean
}

/**
 * Generic NodeListPage component that provides consistent search, filter, and grouping functionality
 * across different node types (projects, tasks, etc.)
 */
export function NodeListPage<
  TNode,
  TFilters extends BaseNodeFiltersState,
  TGroupBy extends string,
>({
  nodes,
  loading,
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
  showFilters,
  onFiltersToggle,
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  config,
  emptyStateTitle,
  emptyStateDescription,
  additionalActions,
  className,
  disableSearchIntegration = false,
}: NodeListPageProps<TNode, TFilters, TGroupBy>) {
  // Helper function to check if any filters are active
  const hasAnyActiveFilters = useCallback(() => {
    if (disableSearchIntegration) {
      // For search results pages, only check filters, not search query
      return Object.entries(filters).some(([key, value]) => {
        // Skip checking certain keys that are not filter values
        if (key === 'id' || key === 'name' || key === 'description') return false
        return value !== 'all' && value !== ''
      })
    }

    return (
      Object.entries(filters).some(([key, value]) => {
        // Skip checking certain keys that are not filter values
        if (key === 'id' || key === 'name' || key === 'description') return false
        return value !== 'all' && value !== ''
      }) ||
      (searchQuery !== '' && searchQuery !== undefined)
    )
  }, [filters, searchQuery, disableSearchIntegration])

  // Apply filtering and search
  const filteredNodes = useMemo(() => {
    const filtered = config.filterNodes(
      nodes,
      activeTab || config.defaultTab || '',
      filters,
      disableSearchIntegration ? '' : searchQuery || '',
      undefined // user parameter is now optional, can be passed from parent if needed
    )

    // Apply sorting if sortNodes function is provided
    return config.sortNodes ? config.sortNodes(filtered) : filtered
  }, [nodes, activeTab, filters, searchQuery, config, disableSearchIntegration])

  // Clear all filters including search
  const handleClearFilters = useCallback(() => {
    onFiltersChange(config.defaultFilters)
    if (!disableSearchIntegration && onSearchChange) {
      onSearchChange('')
    }
  }, [onFiltersChange, onSearchChange, config.defaultFilters, disableSearchIntegration])

  if (loading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="skeleton" skeletonCount={6} />
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full" className={className}>
      <PageHeader
        title={config.title}
        description={config.description}
        action={config.createAction}
      />

      <ListPageLayout
        searchQuery={disableSearchIntegration ? undefined : searchQuery}
        onSearchChange={disableSearchIntegration ? undefined : onSearchChange}
        searchPlaceholder={config.searchPlaceholder}
        tabs={
          config.tabs && config.tabs.length > 0 && activeTab && onTabChange ? (
            <Tabs value={activeTab} onValueChange={onTabChange}>
              <TabGroup tabs={config.tabs} value={activeTab} onValueChange={onTabChange} />
            </Tabs>
          ) : undefined
        }
        actions={
          <div className="flex items-center gap-4">
            <config.GroupingSelectorComponent value={groupBy} onChange={onGroupByChange} />
            <Button variant={showFilters ? 'default' : 'outline'} onClick={onFiltersToggle}>
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasAnyActiveFilters() && (
                <span className="ml-2 bg-primary-foreground text-primary rounded-full px-2 py-0.5 text-xs">
                  Active
                </span>
              )}
            </Button>

            {hasAnyActiveFilters() && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}

            {additionalActions}
          </div>
        }
        filters={
          showFilters && (
            <config.FilterComponent
              filters={filters}
              onFiltersChange={onFiltersChange}
              className="mb-4"
              {...config.filterComponentProps}
            />
          )
        }
      >
        {filteredNodes.length === 0 ? (
          <EmptyState
            icon={config.emptyStateIcon}
            title={emptyStateTitle || `No ${config.nodeNamePlural.toLowerCase()} found`}
            description={
              emptyStateDescription ||
              (hasAnyActiveFilters()
                ? 'Try adjusting your search criteria or filters'
                : `Create a new ${config.nodeName.toLowerCase()} to get started`)
            }
            action={
              !hasAnyActiveFilters() && config.createAction
                ? {
                    label: config.createAction.label,
                    icon: config.createAction.icon,
                    onClick: () => {
                      window.location.href = config.createAction!.href
                    },
                  }
                : undefined
            }
          />
        ) : (
          <config.GroupingComponent
            nodes={config.dataPropName === 'nodes' ? filteredNodes : undefined}
            tasks={config.dataPropName === 'tasks' ? filteredNodes : undefined}
            projects={config.dataPropName === 'projects' ? filteredNodes : undefined}
            messages={config.dataPropName === 'messages' ? filteredNodes : undefined}
            groupBy={groupBy}
            {...(config.groupingComponentProps || {})}
          />
        )}
      </ListPageLayout>
    </PageContainer>
  )
}
